import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getLeaveReports,
  clearError,
  clearReports,
} from "../../../../redux/Slices/authSlice";
import { FaCalendarAlt, FaFileExcel, FaPrint, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import { chunkArray } from "../../../../shared/utils.js/utils";

const LeaveReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { leaveReports, isLoading, error } = useSelector(state => state.auth);
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const locale = "en-US";
  const [leaveDates, setLeaveDates] = useState({
    start_date: "",
    end_date: "",
  });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [allReports, setAllReports] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);
  const pdfContainerRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle("sidenav.reports.leave");
  }, [setTitle]);

  useEffect(() => {
    return () => {
      // Reset when component unmounts
      setLeaveDates({ start_date: "", end_date: "" });
      dispatch(clearReports());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  // Update allReports when leaveReports changes
  useEffect(() => {
    if (leaveReports?.data && Array.isArray(leaveReports.data)) {
      setAllReports(leaveReports.data);
      setCurrentPage(1);
    } else {
      setAllReports([]);
    }
  }, [leaveReports]);

  const handleGenerateReport = async () => {
    if (!leaveDates.start_date || !leaveDates.end_date) {
      toast.error(t("reports.leave.selectDates"), { rtl: isRTL });
      return;
    }
    await dispatch(getLeaveReports(leaveDates));
  };

  const handleApplyPerPage = () => {
    const validValue = Math.max(
      1,
      Math.min(perPageInput, allReports?.length || 1000),
    );
    setPerPage(validValue);
    setCurrentPage(1);
  };

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(allReports.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedReports = allReports.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  // handle export to excel
  const handleExportToExcel = () => {
    if (!allReports || allReports.length === 0) {
      toast.error(t("reports.leave.noDataToExport"), { rtl: isRTL });
      return;
    }

    const data = allReports.map((item, index) => ({
      [t("reports.leave.excel.serial")]: index + 1,
      [t("reports.leave.excel.employee")]:
        item.employee_name || t("reports.leave.notAvailable"),
      [t("reports.leave.excel.department")]:
        item.department || t("reports.leave.notAvailable"),
      [t("reports.leave.excel.leaveType")]:
        item.leave_type || t("reports.leave.notAvailable"),
      [t("reports.leave.excel.startDate")]:
        item.start_date || t("reports.leave.notAvailable"),
      [t("reports.leave.excel.endDate")]:
        item.end_date || t("reports.leave.notAvailable"),
      [t("reports.leave.excel.status")]:
        item.status === "approved"
          ? t("reports.leave.status.approved")
          : item.status === "rejected"
            ? t("reports.leave.status.rejected")
            : item.status === "pending"
              ? t("reports.leave.status.pending")
              : item.status || t("reports.leave.status.unknown"),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("reports.leave.excel.sheetName"));
    XLSX.writeFile(
      wb,
      `${t("reports.leave.excel.fileName")}_${leaveDates.start_date}_${leaveDates.end_date}.xlsx`,
    );
    toast.success(t("reports.leave.exportSuccess"), { rtl: isRTL });
  };

  // handle print to pdf
  const handlePrintPdf = async () => {
    if (!allReports || allReports.length === 0) {
      toast.error(t("reports.leave.print.noData"), { rtl: isRTL });
      return;
    }
    try {
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const chunks = chunkArray(allReports, 25);
      setPdfChunks(chunks);

      // Wait for state update and rendering
      await new Promise(resolve => setTimeout(resolve, 500));

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < chunks.length; i++) {
        const element = document.getElementById(`leave-report-pdf-chunk-${i}`);
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: true,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `leave-report-pdf-chunk-${i}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
              clonedElement.style.setProperty(
                "-webkit-font-smoothing",
                "antialiased",
              );
              clonedElement.style.setProperty(
                "-moz-osx-font-smoothing",
                "grayscale",
              );
              clonedElement.style.textRendering = "optimizeLegibility";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        if (i > 0) pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          pdfHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(
        `ACE_Leave_Report_${leaveDates.start_date}_${leaveDates.end_date}.pdf`,
      );
    } catch (error) {
      console.error("Leave Report PDF error:", error);
      toast.error(t("reports.leave.print.error"), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      {/* العنوان */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            marginBottom: "20px",
            textAlign,
          }}
        >
          {t("reports.leave.title")}
        </h1>
      </div>

      {/* اختيار التاريخ */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #333",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: "white",
                fontSize: "14px",
                fontWeight: "500",
                textAlign,
                direction: dir,
              }}
            >
              {t("reports.leave.startDate")}
            </label>
            <input
              type="date"
              value={leaveDates.start_date}
              onChange={e =>
                setLeaveDates({ ...leaveDates, start_date: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: "white",
                fontSize: "14px",
                fontWeight: "500",
                textAlign,
                direction: dir,
              }}
            >
              {t("reports.leave.endDate")}
            </label>
            <input
              type="date"
              value={leaveDates.end_date}
              onChange={e =>
                setLeaveDates({ ...leaveDates, end_date: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? "#666" : "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              opacity: isLoading ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaCalendarAlt />
            {isLoading
              ? t("reports.leave.generating")
              : t("reports.leave.showReport")}
          </button>
          {allReports && allReports.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <label
                  style={{
                    color: "white",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("common.perPage", { defaultValue: "عدد السجلات" })}:
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <button
                    onClick={() =>
                      setPerPageInput(Math.max(1, perPageInput - 1))
                    }
                    style={{
                      backgroundColor: "#1b3350",
                      color: "white",
                      border: "1px solid #333",
                      padding: "6px 10px",
                      borderRadius: "6px 0 0 6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={allReports?.length || 1000}
                    value={perPageInput}
                    onChange={e =>
                      setPerPageInput(
                        Math.max(1, parseInt(e.target.value) || 1),
                      )
                    }
                    style={{
                      width: "70px",
                      padding: "6px 10px",
                      backgroundColor: "#1a1f2e",
                      border: "1px solid #333",
                      borderLeft: "none",
                      borderRight: "none",
                      borderRadius: 0,
                      color: "white",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                  <button
                    onClick={() =>
                      setPerPageInput(
                        Math.min(allReports?.length || 1000, perPageInput + 1),
                      )
                    }
                    style={{
                      backgroundColor: "#1b3350",
                      color: "white",
                      border: "1px solid #333",
                      padding: "6px 10px",
                      borderRadius: "0 6px 6px 0",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                    }}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={handleApplyPerPage}
                  style={{
                    backgroundColor: "#AC2000",
                    color: "white",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("common.apply", { defaultValue: "تطبيق" })}
                </button>
              </div>
              <button
                onClick={() => setIsPreviewOpen(true)}
                style={{
                  backgroundColor: "#444",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <FaEye />
                {t("reports.leave.preview")}
              </button>
              <button
                onClick={handlePrintPdf}
                disabled={isGeneratingPdf}
                style={{
                  backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  opacity: isGeneratingPdf ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <FaPrint />
                {isGeneratingPdf
                  ? t("reports.leave.print.generating")
                  : t("common.print", {
                      defaultValue: isRTL ? "طباعة PDF" : "Print PDF",
                    })}
              </button>
              <button
                onClick={handleExportToExcel}
                style={{
                  backgroundColor: "#ec4899",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexDirection: isRTL ? "row" : "row-reverse",
                }}
              >
                <FaFileExcel />
                {t("reports.leave.exportExcel")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* عرض النتائج */}
      {allReports && allReports.length > 0 ? (
        <div
          style={{
            backgroundColor: "#1b3350",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #333",
          }}
        >
          <div style={{ overflow: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                direction: dir,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #ec4899" }}>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "60px",
                    }}
                  >
                    {t("reports.leave.table.number")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "150px",
                    }}
                  >
                    {t("reports.leave.table.employee")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "200px",
                    }}
                  >
                    {t("reports.leave.table.department")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "150px",
                    }}
                  >
                    {t("reports.leave.table.leaveType")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "140px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("reports.leave.table.startDate")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "140px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("reports.leave.table.endDate")}
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      minWidth: "140px",
                    }}
                  >
                    {t("reports.leave.table.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((item, index) => (
                  <tr
                    key={item.leave_request_id || index}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                      }}
                    >
                      {(currentPageSafe - 1) * perPage + index + 1}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.employee_name ||
                        (item.employee
                          ? (isRTL
                              ? item.employee.name_ar
                              : item.employee.name_en) || item.employee.name
                          : t("reports.leave.notAvailable"))}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                        whiteSpace: "normal",
                        lineHeight: "1.5",
                        direction: dir,
                      }}
                    >
                      {item.department ||
                        (item.employee?.department
                          ? (isRTL
                              ? item.employee.department.name_ar
                              : item.employee.department.name_en) ||
                            item.employee.department.name
                          : t("reports.leave.notAvailable"))}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                        direction: dir,
                      }}
                    >
                      {item.leave_type || t("reports.leave.notAvailable")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                        whiteSpace: "nowrap",
                        direction: dir,
                      }}
                    >
                      {item.start_date
                        ? new Date(item.start_date).toLocaleDateString(locale)
                        : t("reports.leave.notAvailable")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                        whiteSpace: "nowrap",
                        direction: dir,
                      }}
                    >
                      {item.end_date
                        ? new Date(item.end_date).toLocaleDateString(locale)
                        : t("reports.leave.notAvailable")}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        color: "white",
                      }}
                    >
                      <span
                        style={{
                          padding: "6px 18px",
                          borderRadius: "20px",
                          backgroundColor:
                            item.status === "approved"
                              ? "rgba(172, 32, 0, 0.2)"
                              : item.status === "rejected"
                                ? "rgba(220, 53, 69, 0.2)"
                                : "rgba(255, 193, 7, 0.2)",
                          color: "white",
                          fontSize: "13px",
                          fontWeight: "bold",
                          display: "inline-block",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.status === "approved"
                          ? t("reports.leave.status.approved")
                          : item.status === "rejected"
                            ? t("reports.leave.status.rejected")
                            : item.status === "pending"
                              ? t("reports.leave.status.pending")
                              : item.status ||
                                t("reports.leave.status.unknown")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                marginTop: "20px",
                paddingBottom: "20px",
                direction: dir,
              }}
            >
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                disabled={currentPageSafe === 1}
                style={{
                  padding: "8px 12px",
                  backgroundColor: currentPageSafe === 1 ? "#333" : "#ec4899",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                }}
              >
                {t("common.previous", {
                  defaultValue: isRTL ? "السابق" : "Previous",
                })}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  page =>
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPageSafe - 2 &&
                      page <= currentPageSafe + 2),
                )
                .map((page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${page}`}>
                        <span style={{ padding: "8px", color: "white" }}>
                          ...
                        </span>
                        <button
                          onClick={() => setCurrentPage(page)}
                          style={{
                            padding: "8px 12px",
                            backgroundColor:
                              page === currentPageSafe ? "#ec4899" : "#1a1f2e",
                            color: "white",
                            border: "1px solid #333",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor:
                          page === currentPageSafe ? "#ec4899" : "#1a1f2e",
                        color: "white",
                        border: "1px solid #333",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                }
                disabled={currentPageSafe === totalPages}
                style={{
                  padding: "8px 12px",
                  backgroundColor:
                    currentPageSafe === totalPages ? "#333" : "#ec4899",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    currentPageSafe === totalPages ? "not-allowed" : "pointer",
                }}
              >
                {t("common.next", { defaultValue: isRTL ? "التالي" : "Next" })}
              </button>
            </div>
          )}
        </div>
      ) : leaveReports &&
        (!allReports || allReports.length === 0) &&
        !isLoading ? (
        <div
          style={{
            backgroundColor: "#1b3350",
            borderRadius: "12px",
            padding: "40px",
            border: "1px solid #333",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              color: "#888",
              fontWeight: "500",
            }}
          >
            {t("reports.leave.noData")}
          </div>
        </div>
      ) : null}

      {/* PDF Template - Hidden */}
      {allReports && allReports.length > 0 && (
        <div
          style={{ position: "absolute", top: "-99999px", left: "-99999px" }}
        >
          {chunkArray(allReports, 25).map((chunk, chunkIdx, allChunks) => (
            <div
              key={chunkIdx}
              id={`leave-report-pdf-chunk-${chunkIdx}`}
              style={{
                width: "794px",
                height: "1123px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                direction: dir,
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "1043px",
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    height: "1033px",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    direction: dir,
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "600px",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.1,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      minHeight: "100%",
                    }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                        flexShrink: 0,
                      }}
                      crossOrigin="anonymous"
                    />
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{ textAlign: "center", marginBottom: "16px" }}
                      >
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#0d2a50",
                            direction: dir,
                          }}
                        >
                          {t("reports.leave.print.title", {
                            defaultValue: isRTL
                              ? "تقرير الإجازات"
                              : "Leave Requests Report",
                          })}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginTop: "4px",
                            direction: dir,
                          }}
                        >
                          {t("reports.leave.print.from", {
                            defaultValue: isRTL ? "من" : "From",
                          })}{" "}
                          {leaveDates.start_date
                            ? new Date(
                                leaveDates.start_date,
                              ).toLocaleDateString(locale)
                            : ""}{" "}
                          {t("reports.leave.print.to", {
                            defaultValue: isRTL ? "إلى" : "To",
                          })}{" "}
                          {leaveDates.end_date
                            ? new Date(leaveDates.end_date).toLocaleDateString(
                                locale,
                              )
                            : ""}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#555",
                            marginTop: "2px",
                            direction: dir,
                          }}
                        >
                          {t("reports.leave.print.recordsCount", {
                            defaultValue: isRTL
                              ? "عدد السجلات"
                              : "Number of Records",
                          })}
                          : {allReports.length}
                        </div>
                      </div>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "12px",
                          marginBottom: "18px",
                          direction: dir,
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "#e3f2fd",
                              color: "#0d2a50",
                            }}
                          >
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.serial", {
                                defaultValue: isRTL ? "م" : "Serial",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {t("reports.leave.print.employee", {
                                defaultValue: isRTL ? "الموظف" : "Employee",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {t("reports.leave.print.department", {
                                defaultValue: isRTL ? "القسم" : "Department",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {t("reports.leave.print.leaveType", {
                                defaultValue: isRTL
                                  ? "نوع الإجازة"
                                  : "Leave Type",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.startDate", {
                                defaultValue: isRTL
                                  ? "تاريخ البداية"
                                  : "Start Date",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.endDate", {
                                defaultValue: isRTL
                                  ? "تاريخ النهاية"
                                  : "End Date",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.status", {
                                defaultValue: isRTL ? "الحالة" : "Status",
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((item, index) => (
                            <tr key={item.leave_request_id || index}>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {chunkIdx * 25 + index + 1}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  wordBreak: "break-all",
                                  maxWidth: "150px",
                                  whiteSpace: "normal",
                                }}
                              >
                                {item.employee_name ||
                                  (item.employee
                                    ? (isRTL
                                        ? item.employee.name_ar
                                        : item.employee.name_en) ||
                                      item.employee.name
                                    : "—")}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  wordBreak: "break-all",
                                  maxWidth: "150px",
                                  whiteSpace: "normal",
                                }}
                              >
                                {item.department ||
                                  (item.employee?.department
                                    ? (isRTL
                                        ? item.employee.department.name_ar
                                        : item.employee.department.name_en) ||
                                      item.employee.department.name
                                    : "—")}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {item.leave_type || "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {item.start_date
                                  ? new Date(
                                      item.start_date,
                                    ).toLocaleDateString(locale)
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {item.end_date
                                  ? new Date(item.end_date).toLocaleDateString(
                                      locale,
                                    )
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {item.status === "approved"
                                  ? t("reports.leave.print.approved", {
                                      defaultValue: isRTL
                                        ? "موافق عليه"
                                        : "Approved",
                                    })
                                  : item.status === "rejected"
                                    ? t("reports.leave.print.rejected", {
                                        defaultValue: isRTL
                                          ? "مرفوض"
                                          : "Rejected",
                                      })
                                    : item.status === "pending"
                                      ? t("reports.leave.print.pending", {
                                          defaultValue: isRTL
                                            ? "قيد الانتظار"
                                            : "Pending",
                                        })
                                      : item.status || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div
                      style={{
                        marginTop: "auto",
                        flexShrink: 0,
                        paddingTop: "10px",
                      }}
                    >
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "4px",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        <div
                          style={{
                            fontStyle: "italic",
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            direction: "ltr",
                          }}
                        >
                          <span>Page</span>
                          <strong style={{ fontSize: "13px" }}>
                            {chunkIdx + 1} / {allChunks.length}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PdfPreviewModal
        isOpen={isPreviewOpen && allReports && allReports.length > 0}
        title={t("reports.leave.title")}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintPdf}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          id="preview-leave-report-pdf"
          style={{
            width: "794px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily: '"Times New Roman", "Times", serif',
            boxSizing: "border-box",
            direction: dir,
            transform: "scale(1.0)",
            transformOrigin: "top center",
          }}
        >
          {chunkArray(allReports, 25).map((chunk, chunkIdx, allChunks) => (
            <div
              key={chunkIdx}
              style={{
                width: "794px",
                height: "1123px",
                padding: "40px",
                boxSizing: "border-box",
                backgroundColor: "#ffffff",
                marginBottom: chunkIdx < allChunks.length - 1 ? "20px" : "0",
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "1043px",
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    height: "1033px",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    direction: dir,
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "600px",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.1,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      minHeight: "100%",
                    }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                        flexShrink: 0,
                      }}
                      crossOrigin="anonymous"
                    />
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{ textAlign: "center", marginBottom: "16px" }}
                      >
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#0d2a50",
                            direction: dir,
                          }}
                        >
                          {t("reports.leave.print.title", {
                            defaultValue: isRTL
                              ? "تقرير الإجازات"
                              : "Leave Requests Report",
                          })}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginTop: "4px",
                            direction: dir,
                          }}
                        >
                          {t("reports.leave.print.from", {
                            defaultValue: isRTL ? "من" : "From",
                          })}{" "}
                          {leaveDates.start_date
                            ? new Date(
                                leaveDates.start_date,
                              ).toLocaleDateString(locale)
                            : ""}{" "}
                          {t("reports.leave.print.to", {
                            defaultValue: isRTL ? "إلى" : "To",
                          })}{" "}
                          {leaveDates.end_date
                            ? new Date(leaveDates.end_date).toLocaleDateString(
                                locale,
                              )
                            : ""}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#555",
                            marginTop: "2px",
                            direction: dir,
                          }}
                        >
                          {t("reports.leave.print.recordsCount", {
                            defaultValue: isRTL
                              ? "عدد السجلات"
                              : "Number of Records",
                          })}
                          : {allReports.length}
                        </div>
                      </div>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "12px",
                          marginBottom: "18px",
                          direction: dir,
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "#e3f2fd",
                              color: "#0d2a50",
                            }}
                          >
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.serial", {
                                defaultValue: isRTL ? "م" : "Serial",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {t("reports.leave.print.employee", {
                                defaultValue: isRTL ? "الموظف" : "Employee",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {t("reports.leave.print.department", {
                                defaultValue: isRTL ? "القسم" : "Department",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {t("reports.leave.print.leaveType", {
                                defaultValue: isRTL
                                  ? "نوع الإجازة"
                                  : "Leave Type",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.startDate", {
                                defaultValue: isRTL
                                  ? "تاريخ البداية"
                                  : "Start Date",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.endDate", {
                                defaultValue: isRTL
                                  ? "تاريخ النهاية"
                                  : "End Date",
                              })}
                            </th>
                            <th
                              style={{
                                border: "1px solid #000",
                                padding: "8px",
                                direction: dir,
                                textAlign: "center",
                              }}
                            >
                              {t("reports.leave.print.status", {
                                defaultValue: isRTL ? "الحالة" : "Status",
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((item, index) => (
                            <tr key={item.leave_request_id || index}>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {chunkIdx * 25 + index + 1}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  wordBreak: "break-all",
                                  maxWidth: "150px",
                                  whiteSpace: "normal",
                                }}
                              >
                                {item.employee_name ||
                                  (item.employee
                                    ? (isRTL
                                        ? item.employee.name_ar
                                        : item.employee.name_en) ||
                                      item.employee.name
                                    : "—")}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  wordBreak: "break-all",
                                  maxWidth: "150px",
                                  whiteSpace: "normal",
                                }}
                              >
                                {item.department ||
                                  (item.employee?.department
                                    ? (isRTL
                                        ? item.employee.department.name_ar
                                        : item.employee.department.name_en) ||
                                      item.employee.department.name
                                    : "—")}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {item.leave_type || "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {item.start_date
                                  ? new Date(
                                      item.start_date,
                                    ).toLocaleDateString(locale)
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {item.end_date
                                  ? new Date(item.end_date).toLocaleDateString(
                                      locale,
                                    )
                                  : "—"}
                              </td>
                              <td
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px",
                                  textAlign: "center",
                                  direction: dir,
                                }}
                              >
                                {item.status === "approved"
                                  ? t("reports.leave.print.approved", {
                                      defaultValue: isRTL
                                        ? "موافق عليه"
                                        : "Approved",
                                    })
                                  : item.status === "rejected"
                                    ? t("reports.leave.print.rejected", {
                                        defaultValue: isRTL
                                          ? "مرفوض"
                                          : "Rejected",
                                      })
                                    : item.status === "pending"
                                      ? t("reports.leave.print.pending", {
                                          defaultValue: isRTL
                                            ? "قيد الانتظار"
                                            : "Pending",
                                        })
                                      : item.status || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div
                      style={{
                        marginTop: "auto",
                        flexShrink: 0,
                        paddingTop: "10px",
                      }}
                    >
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "4px",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        <div
                          style={{
                            fontStyle: "italic",
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            direction: "ltr",
                          }}
                        >
                          <span>Page</span>
                          <strong style={{ fontSize: "13px" }}>
                            {chunkIdx + 1} / {allChunks.length}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

export default LeaveReport;
